/**
 * Token model for the reader page text.
 *
 * The server stores book text as spannified HTML chunks (each word wrapped in
 * `<span data-p=N>`). Instead of injecting that HTML into the DOM and mutating
 * it imperatively, the webapp reader parses it once into this token tree and
 * renders it declaratively with Preact. The tree is pristine and immutable for
 * the lifetime of a page: translations, selections, and highlights are all
 * separate state layered on top at render time, so "restoring the original
 * text" is simply rendering without that state.
 */
import * as languageMap from "shared/languageMap";
import { letter, mark } from "shared/unicodePatterns";
import * as spanUtils from "spanUtils";
import * as wordSegmenter from "wordSegmenter";

export type WordToken = {
  kind: "word";
  position: number;
  text: string;
};

export type TextToken = {
  kind: "text";
  text: string;
};

export type ElementToken = {
  kind: "element";
  /**
   * Stable identity assigned at parse time. Renderers key rebuilt wrapper
   * elements by this id so that re-renders which drop leading items (e.g.
   * pagination advancing past the first line) preserve the DOM nodes of
   * everything that remains, instead of re-keying and recreating subtrees.
   */
  id: number;
  tag: string;
  attributes: { [name: string]: string };
  children: ReaderToken[];
};

export type ReaderToken = WordToken | TextToken | ElementToken;

/**
 * A leaf in the depth-first walk of the token tree: a word, a text run, or a
 * childless element (`<br>`, `<img>`, an empty `<p>`, ...). Container elements
 * never appear as items - they are represented by the `chain` of every item
 * inside them, which lets the renderer rebuild the exact tree (including
 * partial inline wrappers around a phrase group) from a flat slice of items.
 */
export type ReaderTextItem = {
  token: ReaderToken;
  /** Ancestor element tokens, outermost first, from the top level down to the item's parent. */
  chain: ElementToken[];
  /**
   * Number of leading `chain` entries up to and including the nearest
   * block-level ancestor (0 when the item sits directly in unwrapped top-level
   * content). Phrase groups are rendered as children of the block root, i.e.
   * at this depth.
   */
  blockDepth: number;
  /** Identifies the nearest block-level ancestor; grouping never crosses blocks. */
  blockIndex: number;
  /** Number of `<br>` items before this one within the same block: a soft paragraph index. */
  brIndex: number;
  index: number;
};

export type ParsedReaderText = {
  blocks: ReaderToken[];
  items: ReaderTextItem[];
  /** Items of each top-level token, in order (parallel to `blocks`). */
  itemsByBlock: ReaderTextItem[][];
  itemIndexByPosition: Map<number, number>;
  /** The word each character belongs to, for characters grouped into words (see `wordRanges`). */
  wordRangeByPosition: Map<number, PositionRange>;
  firstWordPosition?: number;
  lastWordPosition?: number;
};

export type PositionRange = { from: number; to: number };

export type ContextSentenceNode = {
  text: string;
  range?: PositionRange;
};

export type ContextSentence = {
  nodes: ContextSentenceNode[];
  text: string;
  start: number;
  end: number;
};

const blockTagsRegExp =
  /^(address|article|aside|blockquote|dd|div|dl|dt|fieldset|figcaption|figure|footer|form|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|td|th|ul)$/i;

/**
 * Tags dropped (with their entire subtrees) at parse time. The old reader
 * injected stored chunks via `innerHTML`, which keeps `<script>` inert - but
 * this token tree is re-created as live Preact nodes, and a dynamically
 * created `<script>` executes when mounted. `<template>`/`<noscript>` children
 * are similarly hidden by the `innerHTML` parser but would become visible,
 * live DOM if rebuilt. The rest are active or non-rendering content
 * (embedding, metadata, styling) that stored book text has no business
 * carrying into the app page.
 */
const droppedTagsRegExp =
  /^(script|noscript|template|style|link|meta|base|iframe|frame|frameset|object|embed|applet)$/;

/**
 * Attributes dropped at parse time. Element tokens are rebuilt by spreading
 * their attributes as Preact props, where these names are not plain
 * attributes: `on*` values are installed as event listeners, and
 * `ref`/`key`/`dangerouslySetInnerHTML` are intercepted by Preact itself.
 * Stored HTML must not be able to reach any of those live paths.
 */
const isDroppedAttribute = (name: string): boolean =>
  /^(on|ref$|key$|dangerouslysetinnerhtml$)/i.test(name);

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

let elementIdCounter = 0;

const domNodeToToken = (node: Node): ReaderToken | undefined => {
  if (node.nodeType === TEXT_NODE) {
    return { kind: "text", text: node.textContent ?? "" };
  }
  if (node.nodeType !== ELEMENT_NODE) {
    return undefined;
  }

  const element = node as Element;
  const tag = element.tagName.toLowerCase();
  if (droppedTagsRegExp.test(tag)) {
    return undefined;
  }
  const dataPosition = element.getAttribute("data-p");
  if (tag === "span" && dataPosition !== null) {
    return {
      kind: "word",
      position: parseInt(dataPosition, 10),
      text: element.textContent ?? "",
    };
  }

  const attributes: { [name: string]: string } = {};
  for (const attribute of Array.from(element.attributes)) {
    if (!isDroppedAttribute(attribute.name)) {
      attributes[attribute.name] = attribute.value;
    }
  }
  const children: ReaderToken[] = [];
  for (const child of Array.from(element.childNodes)) {
    const childToken = domNodeToToken(child);
    if (childToken) {
      children.push(childToken);
    }
  }
  return { kind: "element", id: elementIdCounter++, tag, attributes, children };
};

const isBlockElement = (token: ElementToken) =>
  blockTagsRegExp.test(token.tag) ||
  token.attributes["data-readlang-block"] !== undefined;

/**
 * Spannify wraps each character of a language written without spaces in a
 * span of its own, and their positions are stored (reading progress, audio
 * sync, saved phrases), so they must stay as they are. Instead, characters are
 * grouped into words here, at runtime, with Intl.Segmenter, segmenting each
 * block's whole text for context. Only adjacent character spans join a word:
 * punctuation or other text between them, or a span of another script, breaks
 * it. Without Intl.Segmenter each character stays a word of its own.
 */
const wordRanges = (items: ReaderTextItem[], language: string) => {
  // By script extension, unlike spannify's `cjkThai`, to include marks which
  // the scripts share, like the prolonged sound mark in スーパー
  const characterRegExp =
    /[\p{scx=Han}\p{scx=Hiragana}\p{scx=Katakana}\p{scx=Thai}]/u;
  const isCharacter = (item: ReaderTextItem | undefined) =>
    item?.token.kind === "word" && characterRegExp.test(item.token.text);
  const itemsByBlockIndex = new Map<number, ReaderTextItem[]>();
  for (const item of items) {
    if (!itemsByBlockIndex.has(item.blockIndex)) {
      itemsByBlockIndex.set(item.blockIndex, []);
    }
    itemsByBlockIndex.get(item.blockIndex)!.push(item);
  }

  const wordRangeByPosition = new Map<number, PositionRange>();
  let word: number[] = [];
  const endWord = () => {
    if (word.length > 1) {
      const range = { from: word[0], to: word[word.length - 1] };
      for (const position of word) {
        wordRangeByPosition.set(position, range);
      }
    }
    word = [];
  };
  for (const blockItems of itemsByBlockIndex.values()) {
    const boundaries = new Set<number>();
    for (const { segment, index } of wordSegmenter.segment(
      blockItems.map(itemText).join(""),
      language
    )) {
      boundaries.add(index);
      boundaries.add(index + segment.length);
    }
    let offset = 0;
    for (const [index, item] of blockItems.entries()) {
      const previous = blockItems[index - 1];
      if (
        !isCharacter(item) ||
        !isCharacter(previous) ||
        item.index !== previous.index + 1 ||
        boundaries.has(offset)
      ) {
        endWord();
      }
      if (isCharacter(item)) {
        word.push((item.token as WordToken).position);
      }
      offset += itemText(item).length;
    }
    endWord();
  }
  return wordRangeByPosition;
};

export const parseReaderText = (
  html: string,
  language?: string
): ParsedReaderText => {
  const doc = document.implementation.createHTMLDocument("");
  const container = doc.createElement("div");
  container.innerHTML = html;

  const blocks: ReaderToken[] = [];
  for (const node of Array.from(container.childNodes)) {
    const token = domNodeToToken(node);
    if (token) {
      blocks.push(token);
    }
  }

  const items: ReaderTextItem[] = [];
  const itemsByBlock: ReaderTextItem[][] = [];
  const itemIndexByPosition = new Map<number, number>();
  let firstWordPosition: number | undefined;
  let lastWordPosition: number | undefined;
  let blockCounter = 0;
  const brCountByBlock = new Map<number, number>();

  const visit = (
    token: ReaderToken,
    chain: ElementToken[],
    blockIndex: number,
    blockDepth: number,
    blockItems: ReaderTextItem[]
  ) => {
    if (token.kind === "element" && token.children.length > 0) {
      let childBlockIndex = blockIndex;
      let childBlockDepth = blockDepth;
      if (isBlockElement(token)) {
        blockCounter += 1;
        childBlockIndex = blockCounter;
        childBlockDepth = chain.length + 1;
      }
      const childChain = [...chain, token];
      for (const child of token.children) {
        visit(child, childChain, childBlockIndex, childBlockDepth, blockItems);
      }
      return;
    }

    const brIndex = brCountByBlock.get(blockIndex) ?? 0;
    const item: ReaderTextItem = {
      token,
      chain,
      blockDepth,
      blockIndex,
      brIndex,
      index: items.length,
    };
    items.push(item);
    blockItems.push(item);

    if (token.kind === "element" && token.tag === "br") {
      brCountByBlock.set(blockIndex, brIndex + 1);
    }
    if (token.kind === "word") {
      itemIndexByPosition.set(token.position, item.index);
      if (firstWordPosition === undefined) {
        firstWordPosition = token.position;
      }
      lastWordPosition = token.position;
    }
  };

  // A page fragment sliced mid-paragraph (see getBookPage) has no enclosing
  // block element, leaving its word spans as top-level nodes. Coalesce each
  // run of consecutive top-level inline nodes into one synthetic block, so a
  // phrase group spanning them renders as a single span instead of one
  // full-translation fragment per word.
  const isTopLevelBlock = (token: ReaderToken) =>
    token.kind === "element" && isBlockElement(token);

  let blockStart = 0;
  while (blockStart < blocks.length) {
    const blockItems: ReaderTextItem[] = [];
    if (isTopLevelBlock(blocks[blockStart])) {
      visit(blocks[blockStart], [], 0, 0, blockItems);
      blockStart++;
    } else {
      blockCounter += 1;
      const syntheticBlockIndex = blockCounter;
      while (
        blockStart < blocks.length &&
        !isTopLevelBlock(blocks[blockStart])
      ) {
        visit(blocks[blockStart], [], syntheticBlockIndex, 0, blockItems);
        blockStart++;
      }
    }
    itemsByBlock.push(blockItems);
  }

  return {
    blocks,
    items,
    itemsByBlock,
    itemIndexByPosition,
    wordRangeByPosition:
      language && languageMap.map[language]?.noWordBoundaries
        ? wordRanges(items, language)
        : new Map(),
    firstWordPosition,
    lastWordPosition,
  };
};

export const getWordItem = (
  parsed: ParsedReaderText,
  position: number
): ReaderTextItem | undefined => {
  const index = parsed.itemIndexByPosition.get(position);
  return index === undefined ? undefined : parsed.items[index];
};

/** The word at a position: its characters, when grouped into a word, or else the position alone */
export const getWordRange = (
  parsed: ParsedReaderText,
  position: number
): PositionRange =>
  parsed.wordRangeByPosition.get(position) ?? { from: position, to: position };

/**
 * The item indexes spanned by a position range, or undefined when either
 * endpoint is outside the parsed text (e.g. a group left over from a page the
 * reader has since navigated away from).
 */
export const getItemRange = (
  parsed: ParsedReaderText,
  range: PositionRange
): { startIndex: number; endIndex: number } | undefined => {
  const startItem = getWordItem(parsed, range.from);
  const endItem = getWordItem(parsed, range.to);
  return startItem && endItem
    ? { startIndex: startItem.index, endIndex: endItem.index }
    : undefined;
};

/**
 * True when a phrase range is structurally valid for grouping: both endpoint
 * words exist, share the same block-level ancestor, and have no `<br>` between
 * them (a `<br>` is treated as a soft paragraph boundary).
 */
export const canGroupRange = (
  parsed: ParsedReaderText,
  from: number,
  to: number
): boolean => {
  if (from === to) {
    return getWordItem(parsed, from) !== undefined;
  }
  const fromItem = getWordItem(parsed, from);
  const toItem = getWordItem(parsed, to);
  if (!fromItem || !toItem) {
    return false;
  }
  return (
    fromItem.blockIndex === toItem.blockIndex &&
    fromItem.brIndex === toItem.brIndex
  );
};

export const inSameBlock = (
  parsed: ParsedReaderText,
  positionA: number,
  positionB: number
): boolean => {
  const itemA = getWordItem(parsed, positionA);
  const itemB = getWordItem(parsed, positionB);
  return (
    itemA !== undefined &&
    itemB !== undefined &&
    itemA.blockIndex === itemB.blockIndex
  );
};

/**
 * True when a sentence break (text containing a sentence terminator such as
 * . ! ? 。 ！ ？ । or a `<br>`) sits between the words at `positionA` and
 * `positionB`. Used by the phrase-merge logic to avoid merging adjacent
 * selections across sentence boundaries.
 */
export const hasTextBreakBetween = (
  parsed: ParsedReaderText,
  positionA: number,
  positionB: number
): boolean => {
  const itemA = getWordItem(parsed, positionA);
  const itemB = getWordItem(parsed, positionB);
  if (!itemA || !itemB) {
    return false;
  }
  const [first, last] =
    itemA.index <= itemB.index ? [itemA, itemB] : [itemB, itemA];
  for (let index = first.index + 1; index < last.index; index++) {
    const token = parsed.items[index].token;
    if (
      token.kind === "text" &&
      spanUtils.sentenceBreakCharacterRegExp.test(token.text)
    ) {
      return true;
    }
    if (token.kind === "element" && token.tag === "br") {
      return true;
    }
  }
  return false;
};

/**
 * The raw text of the range [from, to], including intermediate text runs
 * (spaces, punctuation) between the words. Matches the visible text of the
 * old DOM-extracted phrase fragment.
 */
export const getRangeText = (
  parsed: ParsedReaderText,
  from: number,
  to: number
): string => {
  const fromItem = getWordItem(parsed, from);
  const toItem = getWordItem(parsed, to);
  if (!fromItem || !toItem) {
    return "";
  }
  let text = "";
  for (let index = fromItem.index; index <= toItem.index; index++) {
    const token = parsed.items[index].token;
    if (token.kind === "word" || token.kind === "text") {
      text += token.text;
    }
  }
  return text;
};

const itemText = (item: ReaderTextItem): string =>
  item.token.kind === "word" || item.token.kind === "text"
    ? item.token.text
    : "";

const isSentenceBreakText = (text: string): boolean => {
  if (spanUtils.wordRegExp().test(text[0] as any)) {
    return false;
  }
  return spanUtils.sentenceBreakRegExp().test(text);
};

/**
 * Extract a sentence-sized context window around a word or phrase range.
 *
 * Port of the DOM-walking `spanUtils.getContextSentence` onto the token model:
 * walk alternately backward and forward from the range, stopping at sentence
 * breaks, `<br>`s, or the word budget. `contextBeforePage` is prepended when
 * the window reaches the first word of the page.
 */
export const getContextSentence = (
  parsed: ParsedReaderText,
  range: PositionRange,
  contextBeforePage?: string,
  maxContextWords: number = spanUtils.maxContextWords
): ContextSentence => {
  const fromItem = getWordItem(parsed, range.from);
  const toItem = getWordItem(parsed, range.to);
  if (!fromItem || !toItem) {
    throw Error("error finding context");
  }

  const blockItems = parsed.items.filter(
    (item) => item.blockIndex === fromItem.blockIndex
  );
  const startIndexInBlock = blockItems.findIndex(
    (item) => item.index === fromItem.index
  );
  const endIndexInBlock = blockItems.findIndex(
    (item) => item.index === toItem.index
  );

  let contextStart = range.from;
  let contextEnd = range.to;
  const rangeText = getRangeText(parsed, range.from, range.to);

  const isBr = (item: ReaderTextItem) =>
    item.token.kind === "element" && item.token.tag === "br";
  const isWord = (item: ReaderTextItem) => item.token.kind === "word";

  let before: ContextSentenceNode[] = [];
  const after: ContextSentenceNode[] = [];
  let remainingBudget = maxContextWords;
  let backwardIndex = startIndexInBlock - 1;
  let forwardIndex = endIndexInBlock + 1;
  let canGoBackward = true;
  let canGoForward = !isSentenceBreakText(rangeText);

  // Alternate between collecting backward and forward context to keep the
  // sentence window balanced around the translated word or phrase.
  while (remainingBudget > 0 && (canGoBackward || canGoForward)) {
    if (canGoBackward && backwardIndex >= 0) {
      const item = blockItems[backwardIndex];
      const text = itemText(item);
      const previousItem =
        backwardIndex > 0 ? blockItems[backwardIndex - 1] : undefined;
      const node: ContextSentenceNode = {
        text,
        range: isWord(item)
          ? {
              from: (item.token as WordToken).position,
              to: (item.token as WordToken).position,
            }
          : undefined,
      };

      if (
        isSentenceBreakText(
          `${previousItem ? itemText(previousItem) : ""}${text}`
        ) ||
        isBr(item)
      ) {
        before.push(node);
        canGoBackward = false;
      } else {
        before.push(node);
        if (isWord(item)) {
          contextStart = Math.min(
            contextStart,
            (item.token as WordToken).position
          );
          remainingBudget--;
        }
        backwardIndex--;
      }
    } else {
      canGoBackward = false;
    }

    if (
      canGoForward &&
      forwardIndex < blockItems.length &&
      remainingBudget > 0
    ) {
      const item = blockItems[forwardIndex];
      const text = itemText(item);

      if (isBr(item)) {
        canGoForward = false;
      } else {
        after.push({
          text,
          range: isWord(item)
            ? {
                from: (item.token as WordToken).position,
                to: (item.token as WordToken).position,
              }
            : undefined,
        });
        const nextItem =
          forwardIndex < blockItems.length - 1
            ? blockItems[forwardIndex + 1]
            : undefined;

        if (
          isSentenceBreakText(`${text}${nextItem ? itemText(nextItem) : ""}`)
        ) {
          canGoForward = false;
        } else if (isWord(item)) {
          contextEnd = Math.max(contextEnd, (item.token as WordToken).position);
          remainingBudget--;
        }
        forwardIndex++;
      }
    } else {
      canGoForward = false;
    }
  }

  if (
    contextBeforePage &&
    parsed.firstWordPosition !== undefined &&
    contextStart === parsed.firstWordPosition
  ) {
    before.push({ text: contextBeforePage });
  }

  before = before.reverse();

  const nodes = before
    .concat({ text: rangeText, range: { from: range.from, to: range.to } })
    .concat(after);

  const text = before
    .map((node) => node.text)
    .concat(rangeText)
    .concat(after.map((node) => node.text))
    .join("")
    .replace(/ {2}/g, " ")
    .trim()
    .replace(
      new RegExp(
        `([${spanUtils.sentenceBreakCharacters.join()}])\\s+[${spanUtils.startOfSentencePunctuationCharacters.join()}]+$]*$`
      ),
      "$1"
    )
    .replace(
      new RegExp(`^([${spanUtils.sentenceBreakCharacters.join()}])\\s+`),
      ""
    );

  return {
    nodes,
    text,
    start: contextStart,
    end: contextEnd,
  };
};

/**
 * A sentence break followed by a closing quote (`... Bruno? ».`) ends the
 * sentence at the `?`, leaving `».` as a "sentence" of its own. Read Aloud
 * must not ask a TTS provider to speak such a run: Azure answers with an empty
 * audio clip (which makes the audio element error and stops playback dead) and
 * some browser voices never fire their `end` event for it. A run is speakable
 * when it contains at least one letter, combining mark, or digit.
 */
const speakableRegExp = new RegExp(`[${letter}${mark}0-9]`, "u");

export const hasSpeakableText = (nodes: ContextSentenceNode[]): boolean =>
  nodes.some((node) => speakableRegExp.test(node.text));

/**
 * The sentence containing `position`, as a list of nodes starting at the node
 * containing `position`. Used by Read Aloud to fetch per-word TTS boundaries.
 *
 * Read Aloud can be started from a position that isn't a word on the current
 * page — e.g. a stale resume position, or the `firstPosition ?? 0` fallback
 * firing before the visible range is known on a page that doesn't contain
 * position 0. Degrade to an empty sentence rather than letting
 * `getContextSentence` throw (unlike the translation-context callers, a missing
 * word here is an expected edge, not an invariant violation).
 */
export const getSentenceFromPosition = (
  parsed: ParsedReaderText,
  range: PositionRange,
  position: number,
  contextBeforePage?: string
): ContextSentenceNode[] => {
  if (!getWordItem(parsed, range.from) || !getWordItem(parsed, range.to)) {
    return [];
  }

  const contextSentence = getContextSentence(
    parsed,
    range,
    contextBeforePage,
    400
  );

  const indexOfPosition = contextSentence.nodes.findIndex(
    (node) =>
      node.range && node.range.from <= position && position <= node.range.to
  );

  return contextSentence.nodes.slice(indexOfPosition);
};
